import React, { useContext, useRef, useState } from "react";
import { useBoardFromContext } from "../../../utils/hooks/commonly-stored/uploaded-board/board";
import {
  Board,
  Shipment,
  ShipmentState,
} from "../../../reducers/uploadedBoard";
import {
  FakeLink,
  FlashPCBTable,
  InputLabel,
  TableHeaderCell,
  TableHeaderRow,
  Pill,
} from "../../../constants/Styles";
import Icon from "../../common/Icon";
import Button from "../../common/Button";
import { ButtonTypes, ButtonVariants } from "../../common/Button/constants";
import Checkbox from "../../common/Checkbox";
import { Container } from "../../../reducers/componentSearch";
import ModalContentWithCloseButton from "../../common/ModalContentWithCloseButton";
import Modal from "../../common/Modal";
import UpdateContainerLocation from "../../component-search/PartDrawer/Inventory/PhysicalInventory/UpdateContainerLocation";
import {
  BoardInventoryContainer,
  BoardsCell,
  ContainerItem,
  LabelWithSort,
  MoveContainersAction,
  OptimizationButtonContainer,
  PartRow,
  StatusContainer,
  StepsContainer,
  SubTitle,
  SummaryContainer,
  VerticalList,
} from "./BoardInventoryStyles";
import LoadingIcon from "../../common/LoadingIcon";
import LoadingOverlay from "../../LoadingOverlay";
import useBoardBin from "../../../utils/hooks/commonly-stored/uploaded-board/boardBin";
import StatusSummaryTable from "./StatusSummaryTable";
import PartContainers, {
  allocationQuantitiesByContainer,
} from "./PartContainers";
import useBoardInventory, {
  PartWithInventory,
} from "../../../utils/hooks/inventory/boardInventory";
import useMoveContainers from "../../../utils/hooks/inventory/moveContainers";
import AllocatedQuantityCell from "./AllocatedQuantityCell";
import { formatCurrency } from "../../../utils/valueFormatting";
import {
  ButtonContainer,
  FiltersContainer,
  FiltersRow,
  IconContainer,
  SortingOption,
} from "../../MyAccountPage/Boards/BoardsStyles";
import PlacementTypeCell from "./PlacementTypeCell";
import FeederDetailsCell from "./FeederDetailsCell";
import ManufacturingStatus from "../../ManufacturersPanel/Dashboard/ManufacturingStatus";
import { InventoryBin } from "../parts/Bins/inventoryBinTypes";
import ApproveAllocationButton from "./ApproveAllocationButton";
import PartImage from "../../PartImage";
import PartDrawer, {
  PartDrawerPageMode,
} from "../../component-search/PartDrawer";
import HorizontalScroll from "../../common/HorizontalScroll";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../../../utils/hooks/commonly-stored/uploaded-board/state-machine/reviewBoardFlowLoader";
import SelectShipment from "../../ReviewBoardFlow/Shipments/SelectShipment";
import ShipmentTools from "../../ReviewBoardFlow/Quote/Shipments/ShipmentTools";
import useManufacturingPriority from "../../../utils/hooks/commonly-stored/uploaded-board/manufacturingPriority";
import { CHECK } from "../../../constants/Text";
import { ItemType } from "../parts/InventoryUpdates/MoveContainerAction";
import { useCustomAlert } from "../../../utils/hooks/alerts";
import PartPropertiesUpdate from "./UpdatePartPropertiesCell";
import useDownloadTraveler from "../../../utils/hooks/commonly-stored/uploaded-board/travelerSticker";
import useShipmentsFromContext from "../../../utils/hooks/commonly-stored/uploaded-board/shipments";
import { useStringInput } from "../../../utils/hooks/inputs";
import FormLine from "../../common/FormLine";
import { ToolContainer } from "../FeederReport/FeederReportStyles";

enum SortDirections {
  ASCENDING = "ascending",
  DESCENDING = "descending",
}

enum Filters {
  IN_STOCK,
  ABOVE_PLACEMENT_COUNT,
  NEED_TO_ORDER,
}

export function isCheckedIntoBoardBinAndSatisfiesRequiredAmount(
  part: PartWithInventory,
  boardBin: InventoryBin | null,
  shipmentId: string,
) {
  const allocations = allocationQuantitiesByContainer(part, shipmentId);
  const sumInBoardBin = part.inventory_response.physical_inventory.containers
    .filter((container) => {
      return container.flashpcb_bin_barcode === boardBin?.flashpcb_bin_barcode;
    })
    .reduce((sumInBoardBin, container) => {
      return (
        sumInBoardBin + (allocations[container.flashpcb_container_barcode] || 0)
      );
    }, 0);
  return sumInBoardBin >= part.required_amount;
}

function partHasContainer(part: PartWithInventory, containerBarcode: string) {
  return part.inventory_response.physical_inventory.containers.some(
    (container) => container.flashpcb_container_barcode === containerBarcode,
  );
}

export default function BoardInventory() {
  const { boardId, shipmentId } = useContext(
    BoardIdContext,
  ) as BoardShipmentIds;
  const shipments = useShipmentsFromContext(boardId) as Shipment[];
  const tableRef = useRef<HTMLTableElement>(null);
  const [selectedPartIdForDrawer, setSelectedPartIdForDrawer] = useState<
    string | null
  >(null);
  const boardStuff = useBoardFromContext();
  const board = boardStuff.board as Board;
  const { boardPartsResponse } = useBoardInventory(board.id, shipmentId);
  const { inventoryBin: boardBin, loadingInventoryBin } = useBoardBin(board.id);
  const [selectedContainers, setSelectedContainers] = useState<Container[]>([]);

  const [moveContainersOpen, setMoveContainersOpen] = useState<boolean>(false);
  const [nameSortDirection, setNameSortDirection] = useState<SortDirections>(
    SortDirections.ASCENDING,
  );
  const [partsFilter, setPartsFilter] = useState<Filters | null>(null);
  const searchBarcodeInputRef = useRef<HTMLInputElement>(null);
  const {
    value: barcode,
    reset: resetBarcode,
    bind: bindBarcode,
  } = useStringInput("");
  // scannedContainerBarcode is holding the scanned barcode until a new one is searched because the barcode input field will be immediately cleared and refocused
  const [scannedContainerBarcode, setScannedContainerBarcode] = useState<
    string | null
  >(null);
  const { optimizeAllocationsForShipment } = useManufacturingPriority();
  const customAlert = useCustomAlert();
  const closeDrawer = () => {
    setSelectedPartIdForDrawer(null);
  };
  const downloadTravelerSticker = useDownloadTraveler();

  const selectAllContainersInBoardBin = () => {
    if (boardPartsResponse) {
      setSelectedContainers(
        boardPartsResponse.parts.flatMap((part) =>
          part.inventory_response.physical_inventory.containers.filter(
            (container) =>
              container.flashpcb_bin_barcode === boardBin?.flashpcb_bin_barcode,
          ),
        ),
      );
    }
  };

  const allContainersSelected = () => {
    if (!boardPartsResponse || !boardBin) return false;

    const allContainersInBoardBin = boardPartsResponse.parts.flatMap((part) =>
      part.inventory_response.physical_inventory.containers.filter(
        (container) =>
          container.flashpcb_bin_barcode === boardBin?.flashpcb_bin_barcode,
      ),
    );

    if (allContainersInBoardBin.length === 0) return false;

    return allContainersInBoardBin.every((container) =>
      selectedContainers.some(
        (selectedContainer) =>
          selectedContainer.flashpcb_container_barcode ===
          container.flashpcb_container_barcode,
      ),
    );
  };

  const focusOnInput = () => {
    if (searchBarcodeInputRef.current !== null) {
      searchBarcodeInputRef.current.value = "";
      searchBarcodeInputRef.current.focus();
    }
  };

  const findContainer = () => {
    const containerBarcode = barcode.trim();
    focusOnInput();
    resetBarcode();
    if (
      !boardPartsResponse?.parts.some((part) =>
        partHasContainer(part, containerBarcode),
      )
    ) {
      setScannedContainerBarcode(null);
      customAlert(
        `No part on this board has a container with barcode ${containerBarcode}`,
      );
      return;
    }
    // Show all parts so a filter can't hide the scanned one
    setPartsFilter(null);
    setScannedContainerBarcode(containerBarcode);
  };

  const isScannedPart = (part: PartWithInventory) => {
    return (
      scannedContainerBarcode !== null &&
      partHasContainer(part, scannedContainerBarcode)
    );
  };

  const sortParts = (parts: PartWithInventory[]) => {
    return [...parts].sort((a, b) => {
      // The part with the scanned container goes first
      const scannedA = isScannedPart(a) ? 1 : 0;
      const scannedB = isScannedPart(b) ? 1 : 0;
      if (scannedA !== scannedB) {
        return scannedB - scannedA;
      }
      const partA = a.part?.name || "";
      const partB = b.part?.name || "";
      return nameSortDirection === SortDirections.ASCENDING
        ? partA.localeCompare(partB)
        : partB.localeCompare(partA);
    });
  };

  const closeModal = () => {
    setMoveContainersOpen(false);
  };

  const onFinishedEditing = () => {
    closeModal();
  };

  const onErrorMovingContainers = (detail: string) => {
    customAlert(detail);
  };
  const { movingContainers } = useMoveContainers(
    onFinishedEditing,
    onErrorMovingContainers,
  );

  if (shipmentId === null) {
    return <SelectShipment />;
  }

  if (!boardPartsResponse || loadingInventoryBin || shipments === null) {
    return <LoadingIcon text={"Loading"} centerHorizontally />;
  }

  const shipment = shipments.find((shipment) => {
    return shipment.id == shipmentId;
  }) as Shipment;
  // The store flips the shipment's state as soon as the machine starts
  // reprocessing it and only refetches the shipment once that finishes, so
  // until then everything taken from its shipment parts (designators,
  // placement type, counts, required amounts) may be stale.
  const shipmentUpdating = shipment.state !== ShipmentState.PROCESSED;
  // Allocations come from the part, so anything built on them is stale while
  // the part machine is working on that part too
  const allocationsUpdating = (partWithInventory: PartWithInventory) => {
    return shipmentUpdating || partWithInventory.part.is_updating;
  };

  const TABLE_HEADER_ROW = [
    {
      label: (
        <LabelWithSort
          onClick={() =>
            setNameSortDirection(
              nameSortDirection === SortDirections.ASCENDING
                ? SortDirections.DESCENDING
                : SortDirections.ASCENDING,
            )
          }
        >
          <>Part name</>{" "}
          {nameSortDirection === SortDirections.ASCENDING ? (
            <Icon src={"/icons/triangle_down_gray.svg"} alt={"sort"} />
          ) : (
            <Icon
              src={"/icons/triangle_down_gray.svg"}
              styleProps={{ transform: "rotate(180deg)" }}
              alt={"sort"}
            />
          )}
        </LabelWithSort>
      ),
      width: "14rem",
    },
    {
      label: "Designators",
      width: "8rem",
    },
    {
      label: "Placement type",
      width: "10rem",
    },
    {
      label: "Part properties",
      width: "8rem",
    },
    {
      label: "Reel / feeder details",
      width: "10rem",
    },
    {
      label: "Minimum parts needed",
      width: "8rem",
    },
    {
      label: "Quantity required with attrition / spillage",
      width: "8rem",
    },
    {
      label: "Approve current allocated amount",
      width: "12rem",
    },
    {
      label: "Required qty is in stock",
      width: "6rem",
    },
    {
      label: "Part is in project tote",
      width: "6rem",
    },
    {
      label: "Amount allocated",
      width: "8rem",
    },
    {
      label: "Is consignment?",
      width: "8rem",
    },
    {
      label: "Container details",
      width: "30rem",
    },
  ];

  return (
    <BoardInventoryContainer>
      <LoadingOverlay loading={movingContainers} text={"Moving container..."} />
      <SelectShipment />
      <ShipmentTools shipment={shipment} />
      {board.order_summary !== null && (
        <FakeLink onClick={() => void downloadTravelerSticker(board.id)}>
          Traveler
        </FakeLink>
      )}
      <StatusContainer>
        <SummaryContainer>
          <StatusSummaryTable />
        </SummaryContainer>
        <StepsContainer>
          <ManufacturingStatus />
        </StepsContainer>
      </StatusContainer>
      <OptimizationButtonContainer>
        <Button
          label={"Optimize allocations for this shipment"}
          onClick={() => optimizeAllocationsForShipment(board.id, shipmentId)}
        />
      </OptimizationButtonContainer>
      <FiltersContainer>
        <FiltersRow>
          <IconContainer>
            <Icon src="/icons/filters.svg" alt="sort-by" /> Filter:
          </IconContainer>
          <ButtonContainer>
            <SortingOption
              isSelected={partsFilter === null}
              onClick={() => setPartsFilter(null)}
            >
              All parts
            </SortingOption>
            <SortingOption
              isSelected={partsFilter === Filters.IN_STOCK}
              onClick={() => setPartsFilter(Filters.IN_STOCK)}
            >
              Parts with sufficient inventory
            </SortingOption>
            <SortingOption
              isSelected={partsFilter === Filters.ABOVE_PLACEMENT_COUNT}
              onClick={() => setPartsFilter(Filters.ABOVE_PLACEMENT_COUNT)}
            >
              Parts with maybe sufficient inventory
            </SortingOption>
            <SortingOption
              isSelected={partsFilter === Filters.NEED_TO_ORDER}
              onClick={() => setPartsFilter(Filters.NEED_TO_ORDER)}
            >
              Parts to order
            </SortingOption>
          </ButtonContainer>
        </FiltersRow>
      </FiltersContainer>
      <ToolContainer
        onSubmit={(e) => {
          e.preventDefault();
          findContainer();
        }}
      >
        <FormLine
          name="containerBarcode"
          label="Container barcode"
          placeholder={"Scan code or enter manually"}
          isRequired={true}
          bind={bindBarcode}
          ref={searchBarcodeInputRef}
        />
        <Button label={"Find container"} type={ButtonTypes.SUBMIT} centerText />
        <Button
          label={"Clear"}
          type={ButtonTypes.BUTTON}
          variant={ButtonVariants.SECONDARY}
          centerText
          disabled={scannedContainerBarcode === null}
          onClick={() => {
            resetBarcode();
            setScannedContainerBarcode(null);
          }}
        />
      </ToolContainer>
      <MoveContainersAction>
        <SubTitle>Move containers: </SubTitle>
        <Checkbox
          label={"Select all containers in board bin"}
          checked={allContainersSelected()}
          onChange={() => {
            if (allContainersSelected()) {
              setSelectedContainers([]);
            } else {
              selectAllContainersInBoardBin();
            }
          }}
        />
        <Button
          label={"Move selected containers"}
          variant={ButtonVariants.PRIMARY}
          type={ButtonTypes.BUTTON}
          disabled={selectedContainers.length === 0}
          onClick={() => setMoveContainersOpen(true)}
        />
      </MoveContainersAction>
      <HorizontalScroll childrenRef={tableRef}>
        <FlashPCBTable ref={tableRef}>
          <thead>
            <TableHeaderRow>
              {TABLE_HEADER_ROW.map((column, index) => (
                <TableHeaderCell key={`header-${index}`} width={column.width}>
                  {column.label}
                </TableHeaderCell>
              ))}
            </TableHeaderRow>
          </thead>
          <tbody>
            {sortParts(boardPartsResponse.parts)
              .filter((partWithInventory) => {
                if (partsFilter === Filters.IN_STOCK) {
                  return (
                    partWithInventory.amount_allocated >=
                    partWithInventory.required_amount
                  );
                } else if (partsFilter === Filters.ABOVE_PLACEMENT_COUNT) {
                  return (
                    partWithInventory.num_elements_per_shipment <=
                      partWithInventory.amount_allocated &&
                    partWithInventory.amount_allocated <
                      partWithInventory.required_amount
                  );
                } else if (partsFilter === Filters.NEED_TO_ORDER) {
                  return (
                    partWithInventory.amount_allocated <
                    partWithInventory.num_elements_per_shipment
                  );
                } else {
                  return true;
                }
              })
              .map((partWithInventory, index) => {
                const part = partWithInventory.part;
                const partIsInBoardToteAndSatisfiesRequiredAmount =
                  isCheckedIntoBoardBinAndSatisfiesRequiredAmount(
                    partWithInventory,
                    boardBin,
                    shipmentId,
                  );
                const inventoryIsSufficient =
                  partWithInventory.amount_allocated >=
                  partWithInventory.required_amount;
                // Warn takes precedence over highlight in the row color, so
                // drop it for the scanned part to make that row stand out
                const isScanned = isScannedPart(partWithInventory);
                return (
                  <PartRow
                    key={`${index} ${part.id}`}
                    warn={!inventoryIsSufficient && !isScanned}
                    scanned={isScanned}
                    style={{ border: "1px solid grey" }}
                  >
                    <BoardsCell>
                      <VerticalList>
                        <FakeLink
                          onClick={() => setSelectedPartIdForDrawer(part.id)}
                        >
                          {part.name}
                        </FakeLink>
                        <PartImage part={part} />
                        {partWithInventory.part.manufacturer !== null && (
                          <div>
                            <InputLabel>Manufacturer</InputLabel>
                            <ContainerItem>
                              {partWithInventory.part.manufacturer}
                            </ContainerItem>
                          </div>
                        )}
                        <div>
                          <InputLabel>Short description</InputLabel>
                          <ContainerItem>
                            {partWithInventory.part.short_description}
                          </ContainerItem>
                        </div>
                        {partWithInventory.part.price_info !== null && (
                          <div>
                            <InputLabel>Unit price</InputLabel>
                            <ContainerItem>
                              {formatCurrency(
                                partWithInventory.part.price_info.unit_price,
                                "USD",
                              )}
                            </ContainerItem>
                          </div>
                        )}
                      </VerticalList>
                    </BoardsCell>
                    <BoardsCell>
                      {shipmentUpdating ? (
                        <Pill width={"100%"} />
                      ) : (
                        partWithInventory.element_names.join(", ")
                      )}
                    </BoardsCell>
                    <BoardsCell>
                      <PlacementTypeCell
                        part={partWithInventory}
                        updating={shipmentUpdating}
                      />
                    </BoardsCell>
                    <BoardsCell>
                      <PartPropertiesUpdate part={partWithInventory.part} />
                    </BoardsCell>
                    <BoardsCell>
                      <FeederDetailsCell part={partWithInventory} />
                    </BoardsCell>
                    <BoardsCell>
                      <VerticalList>
                        <InputLabel>Number of placements: </InputLabel>
                        {shipmentUpdating ? (
                          <Pill width={"5rem"} />
                        ) : (
                          <div>
                            {partWithInventory.num_elements_per_shipment}
                          </div>
                        )}
                      </VerticalList>
                    </BoardsCell>
                    <BoardsCell>
                      <AllocatedQuantityCell
                        part={partWithInventory}
                        updating={shipmentUpdating}
                      />
                    </BoardsCell>
                    <BoardsCell>
                      <ApproveAllocationButton
                        part={partWithInventory}
                        refreshing={allocationsUpdating(partWithInventory)}
                      />
                    </BoardsCell>
                    <BoardsCell>
                      <VerticalList>
                        <InputLabel>Inventory is sufficient? </InputLabel>
                        {allocationsUpdating(partWithInventory) ? (
                          <Pill width={"5rem"} />
                        ) : inventoryIsSufficient ? (
                          <Icon src="/icons/check_green.svg" alt="True" />
                        ) : (
                          <Icon src="/icons/fatal.svg" alt="X" />
                        )}
                      </VerticalList>
                    </BoardsCell>
                    <BoardsCell>
                      <VerticalList>
                        <InputLabel>Container in board tote? </InputLabel>
                        {allocationsUpdating(partWithInventory) ? (
                          <Pill width={"5rem"} />
                        ) : partIsInBoardToteAndSatisfiesRequiredAmount ? (
                          <Icon src="/icons/check_green.svg" alt="True" />
                        ) : (
                          <Icon src="/icons/fatal.svg" alt="X" />
                        )}
                      </VerticalList>
                    </BoardsCell>
                    <BoardsCell>
                      <VerticalList>
                        <InputLabel>Allocated: </InputLabel>
                        {partWithInventory.part.is_updating ? (
                          <Pill width={"5rem"} />
                        ) : (
                          <div>
                            {partWithInventory.amount_allocated.toLocaleString()}
                          </div>
                        )}
                      </VerticalList>
                      <VerticalList>
                        <InputLabel>Spent:</InputLabel>
                        {partWithInventory.part.is_updating ? (
                          <Pill width={"5rem"} />
                        ) : (
                          <div>
                            {partWithInventory.amount_spent.toLocaleString()}
                          </div>
                        )}
                      </VerticalList>
                    </BoardsCell>
                    <BoardsCell>
                      {shipmentUpdating ? (
                        <Pill width={"5rem"} />
                      ) : (
                        partWithInventory.is_consignment && CHECK
                      )}
                    </BoardsCell>
                    <BoardsCell>
                      <PartContainers
                        part={partWithInventory}
                        boardBin={boardBin}
                        shipmentId={shipmentId}
                        boardUserId={board.user_id}
                        selectedContainers={selectedContainers}
                        setSelectedContainers={setSelectedContainers}
                      />
                    </BoardsCell>
                  </PartRow>
                );
              })}
          </tbody>
        </FlashPCBTable>
      </HorizontalScroll>
      {moveContainersOpen && (
        <Modal isOpen={moveContainersOpen} onRequestClose={closeModal}>
          <ModalContentWithCloseButton closeModal={closeModal}>
            <UpdateContainerLocation
              selectedContainers={selectedContainers.map(
                (container) => container.flashpcb_container_barcode,
              )}
              itemType={ItemType.PARTS_CONTAINERS}
              onFinishedEditing={onFinishedEditing}
              onCancel={closeModal}
            />
          </ModalContentWithCloseButton>
        </Modal>
      )}
      <PartDrawer
        partId={selectedPartIdForDrawer}
        onClose={closeDrawer}
        initialPageMode={PartDrawerPageMode.INVENTORY}
      />
    </BoardInventoryContainer>
  );
}
